import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { deleteRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import UtilContext from '../../UtilContext';
import { LocaleContext } from '../../LocaleContext';
import ServiceTaskPlanner from '../../planner/ServiceTaskPlanner';

export default function CrmTaskTableLauncher({
  crmTicketId,
  getRequest,
  postRequest,
  employees,
  canManage,
  onSummaryChange,
  taskTheme,
  taskDescription,
  taskSenderId,
  crmTicketType,
}) {
  const [state] = useContext(DatabaseContext);
  const { setDialogText, setVisible, toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [table, setTable] = useState(null);
  const [showPlanner, setShowPlanner] = useState(false);
  const [plannerKey, setPlannerKey] = useState(0);
  const [pickOpen, setPickOpen] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const crmTemplates = (state?.task_templates || []).filter((t) => (t.template_context || 'service') === 'crm');
  const crmStepNameById = (id) => {
    const step = (state?.crm_steps || []).find((x) => String(x.id) === String(id));
    return step?.name?.sk || step?.name?.en || step?.name?.de || step?.name || (id ? `#${id}` : '');
  };

  const notify = (severity, summary, detail) =>
    toast?.current?.show({ severity, summary, detail, life: 2400 });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const tableResponse = await getRequest(`/api/crm_communication_tickets/${crmTicketId}/task_table`);
        const data = (tableResponse && typeof tableResponse === 'object' && 'data' in tableResponse) ? tableResponse.data : tableResponse;
        if (data == null) {
          setTable(null);
          setShowPlanner(false);
        } else {
          setTable(data);
          setShowPlanner(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [crmTicketId, getRequest]);

  const handleTableChange = (next) => {
    if (!next) return;
    const tableData = next?.data ?? next;
    setTable((prev) => ({ ...(prev || {}), ...(tableData || {}) }));
  };

  const openTemplatePicker = async () => {
    setTemplateId(null);
    setTemplatePreview(null);
    setReplaceExisting(false);
    setPickOpen(true);
  };

  const onPickTemplate = (tplId) => {
    setTemplateId(tplId);
    if (!tplId) {
      setTemplatePreview(null);
      return;
    }
    const tpl = crmTemplates.find((x) => String(x.id) === String(tplId));
    setTemplatePreview(tpl ?? null);
  };

  const createEmptyTable = async () => {
    try {
      setDialogText(t('Common.Saving'));
      setVisible(true);
      setLoading(true);
      const fd = new FormData();
      const res = await postRequest(`/api/crm_communication_tickets/${crmTicketId}/task_tables`, fd, true);
      const payload = res?.data ?? res;
      setTable(payload?.data ?? payload);
      setShowPlanner(true);
      notify('success', t('TaskTableLauncher.ToastCreated'), t('TaskTableLauncher.EmptyTableCreated'));
    } catch {
      notify('error', t('Common.Error'), t('TaskTableLauncher.EmptyTableCreateFailed'));
    } finally {
      setVisible(false);
      setLoading(false);
    }
  };

  const deleteEmptyTable = async () => {
    if (!table || (table.rows && table.rows.length > 0)) {
      notify('warn', t('TaskTableLauncher.ToastDeleteRejectedHeader'), t('TaskTableLauncher.DeleteTableOnlyWhenEmpty'));
      return;
    }
    confirmDialog({
      message: t('TaskTableLauncher.ConfirmDeleteEmptyTable'),
      header: t('ServiceTaskPlanner.ConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: async () => {
        try {
          setDialogText(t('TaskTableLauncher.DeletingTable'));
          setVisible(true);
          setLoading(true);
          await deleteRequest(`/api/task_tables/${table.id}`, true);
          setTable(null);
          setShowPlanner(false);
          notify('success', t('TaskTableLauncher.ToastDeleted'), t('TaskTableLauncher.TableDeleted'));
        } catch {
          notify('error', t('Common.Error'), t('TaskTableLauncher.TableDeleteFailed'));
        } finally {
          setVisible(false);
          setLoading(false);
        }
      },
    });
  };

  const applyTemplate = async () => {
    if (!templateId) return;
    try {
      setDialogText(t('TaskTableLauncher.ApplyingTemplate'));
      setVisible(true);
      setLoading(true);
      const fd = new FormData();
      fd.append('template_id', templateId);
      fd.append('reset_rows', replaceExisting ? '1' : '0');
      const res = await postRequest(
        `/api/crm_communication_tickets/${crmTicketId}/task_tables/from_template`,
        fd,
        true,
      );
      const payload = res?.data ?? res;
      const tableData = payload?.data ?? payload;
      const r = await getRequest(`/api/task_tables/${tableData.id}`);
      const full = r?.data ?? r;
      const fresh = full?.data ?? full;
      setTable(fresh);
      setPickOpen(false);
      setShowPlanner(true);
      setPlannerKey((k) => k + 1);
      notify(
        'success',
        t('TaskTableLauncher.TemplateAppliedTitle'),
        replaceExisting ? t('TaskTableLauncher.TemplateAppliedReplaced') : t('TaskTableLauncher.TemplateAppliedAppended'),
      );
    } catch {
      notify('error', t('Common.Error'), t('TaskTableLauncher.TemplateApplyFailed'));
    } finally {
      setVisible(false);
      setLoading(false);
    }
  };

  const employeeNameById = (id) => {
    const u = (employees || []).find((x) => String(x.id) === String(id));
    return u ? (u?.employee?.name ?? u?.username ?? `#${id}`) : (id ? `#${id}` : '');
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
        <div className="flex items-center gap-3">
          <i className="pi pi-table text-blue-700" />
          <div>
            <div className="text-sm text-blue-900 font-semibold">
              {t('TaskTableLauncher.TableTasksPrefix')}
              {table ? ` - ${table.title || t('TaskTableLauncher.NoTableName')}` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!table ? (
            <div className="flex gap-2">
              <Button label={t('TaskTableLauncher.ButtonEmptyTable')} icon="pi pi-plus" onClick={createEmptyTable} disabled={loading || !canManage} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
              <Button label={t('TaskTableLauncher.ButtonFromTemplate')} icon="pi pi-table" onClick={openTemplatePicker} disabled={loading || !canManage} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
            </div>
          ) : (
            <>
              <Button
                label={showPlanner ? t('TaskTableLauncher.ButtonHidePlanner') : t('TaskTableLauncher.ButtonShowPlanner')}
                icon="pi pi-pencil"
                onClick={() => setShowPlanner((v) => !v)}
                disabled={!canManage}
                style={{ textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 6 }}
              />
              <Button
                label={t('TaskTableLauncher.ButtonFromTemplate')}
                icon="pi pi-table"
                onClick={openTemplatePicker}
                disabled={!canManage}
                style={{ textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 6 }}
              />
              {(!table.rows || table.rows.length === 0) ? (
                <Button
                  label={t('TaskTableLauncher.ButtonDeleteTable')}
                  icon="pi pi-trash"
                  severity="danger"
                  onClick={deleteEmptyTable}
                  disabled={!canManage || loading}
                  style={{ textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 6 }}
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      {(table && showPlanner) ? (
        <div className="mt-3 min-w-0">
          <ServiceTaskPlanner
            key={plannerKey}
            serviceTaskId={crmTicketId}
            taskTableId={table.id}
            getRequest={getRequest}
            postRequest={postRequest}
            employees={employees}
            canManage={canManage}
            onTableChange={handleTableChange}
            onSummaryChange={onSummaryChange}
            taskTheme={taskTheme}
            taskDescription={taskDescription}
            taskSenderId={taskSenderId}
            isCrmTaskTable
            crmSteps={state?.crm_steps || []}
            crmTicketType={crmTicketType}
          />
        </div>
      ) : null}

      <Dialog header={t('TaskTableLauncher.PickTemplateHeader')} visible={pickOpen} style={{ width: '44rem' }} onHide={() => setPickOpen(false)}>
        <div className="flex flex-col gap-3">
          <Dropdown
            className="w-full"
            placeholder={t('TaskTableLauncher.PickTemplatePlaceholder')}
            options={crmTemplates.map((x) => ({ label: x.name, value: x.id }))}
            value={templateId}
            onChange={(e) => onPickTemplate(e.value)}
            filter
          />
          {templatePreview?.rows_json?.length > 0 ? (
            <div className="border rounded p-2">
              <div className="text-sm font-medium mb-2">{t('TaskTableLauncher.TemplatePreviewTitle')} - {templatePreview.name}</div>
              <DataTable value={templatePreview.rows_json} size="small" tableStyle={{ minWidth: 600 }}>
                <Column header="#" body={(_r, o) => o.rowIndex + 1} style={{ width: '3.5rem' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewTaskName')} field="title" style={{ width: '260px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewAssignee')} body={(r) => <span>{employeeNameById(r.assignee_id)}</span>} style={{ width: '200px' }} />
                <Column header={t('TaskTemplateAdd.Step')} body={(r) => <span>{crmStepNameById(r.crm_step_id)}</span>} style={{ width: '180px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewDuration')} body={(r) => <span>{`${r.duration_days ?? 0} ${t('ServiceTaskPlanner.DaysShort')}`}</span>} style={{ width: '110px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewChaining')} body={(r) => <span>{r.chain_mode === 'parallel_prev' ? '||' : '->'}</span>} style={{ width: '140px' }} />
              </DataTable>
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-sm mt-1">
            <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />
            {t('TaskTableLauncher.ReplaceExistingLabel')}
          </label>

          <div className="flex justify-end gap-2 mt-2">
            <Button label={t('TaskTableLauncher.ButtonCancel')} severity="secondary" onClick={() => setPickOpen(false)} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
            <Button label={t('TaskTableLauncher.ButtonApplyTemplate')} icon="pi pi-check" onClick={applyTemplate} disabled={!templateId} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

